import axios from "axios";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ReactSelect from "../../../Components/PortalSelect";
import { AddGraphicDesignSchema } from "../../../ValidationSchema";
import Modal from "../../../Components/Modal";

const AddGraphicDesignModal = ({
  show,
  userId,
  orderId,
  onClose,
  fetchProducts,
}) => {
  const Navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [salesExecutiveData, setSalesExecutiveData] = useState([]);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      border: "1px solid #bfc9d4",
      boxShadow: "#000 !important",
      borderRadius: "6px",
      padding: "4px",
      "&:hover": {
        border: "1px solid #bfc9d4",
      },
    }),
    multiValue: (styles) => {
      return {
        ...styles,
        backgroundColor: "#606060",
      };
    },
    multiValueLabel: (styles) => ({
      ...styles,
      color: "#fff",
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: "#000",
      "& > svg": {
        backgroundColor: "#fff",
        borderRadius: "10px",
        marginLeft: "2px",
        marginRight: "2px",
      },
      ":hover": {
        backgroundColor: data.color,
        color: data.color,
      },
    }),
    option: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: "white",
        color: "#000",
        ":hover": {
          backgroundColor: "#f5f5f5",
        },
      };
    },
  };

  const ProductData = () => {
    axios
      .get(`${process.env.REACT_APP_URL}/admin/design-studio/get-all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {})
      .catch((err) => {
        if (err?.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  useEffect(() => {
    ProductData();
  }, []);

  const [initialValues, setInitialValues] = useState({
    order_id: "",
    image: null,
  });

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const previews = selectedFiles.map((file) => ({
      url: URL.createObjectURL(file),
      type: file.type.startsWith("image") ? "image" : "video",
      name: file.name,
      file,
    }));

    const updatedFiles = [...files, ...selectedFiles];
    const updatedPreviews = [...mediaPreviews, ...previews];

    setFiles(updatedFiles);
    setMediaPreviews(updatedPreviews);
    AddDesignStudios.setFieldValue("image", updatedFiles);
  };

  useEffect(() => {
    if (orderId) {
      setInitialValues((prev) => ({
        ...prev,
        order_id: orderId,
      }));
    }
  }, [orderId]);

  const handleThumbnailClick = (index) => {
    setActivePreviewIndex(index);
  };

  // Clean up object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      mediaPreviews.forEach((preview) => {
        URL.revokeObjectURL(preview.url);
      });
    };
  }, [mediaPreviews]);

  const AddDesignStudios = useFormik({
    initialValues,
    enableReinitialize: true,
    // validationSchema: AddGraphicDesignSchema,

    onSubmit: async (values, action) => {
      const user = localStorage.getItem("user");
      const userData = JSON.parse(user);
      const designation = userData?.designation;
      const designer_id = userData?.id;

      const formData = new FormData();

      // Add order_id to formData
      formData.append("order_id", orderId);

      // Add files to formData
      values.image.forEach((file) => {
        formData.append("design_image", file);
      });

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
          "Content-Type": "multipart/form-data",
        },
      };

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_URL}/admin/graphic_designer/design/${orderId}`,
          formData,
          config
        );

        if (res.status === 200) {
          toast.success(res?.data?.message);
          // Clean up and close
          setFiles([]);
          setMediaPreviews([]);
          action.resetForm();

          // Refresh data and close modal
          if (fetchProducts) {
            fetchProducts();
          }
          onClose();
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "File size too large");
      }
    },
  });

  return (
    <Modal
      open={!!show}
      onClose={onClose}
      title="Add Design"
      subtitle="Upload the mockups for this order."
      size="md"
      footer={
        <>
          <button type="button" className="al-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="al-btn al-btn--primary"
            onClick={AddDesignStudios.handleSubmit}
            disabled={AddDesignStudios.isSubmitting}
          >
            {AddDesignStudios.isSubmitting ? "Uploading…" : "Upload designs"}
          </button>
        </>
      }
    >
      <div className="al-field">
        <label className="al-label">Design images</label>

        <label className="al-drop">
          <input
            type="file"
            name="image"
            multiple
            accept="image/*,video/*"
            onChange={handleFileChange}
            onBlur={AddDesignStudios.handleBlur}
            style={{ display: "none" }}
          />
          <span className="al-drop__icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
          </span>
          <span className="al-drop__title">Drag images here, or click to browse</span>
          <span className="al-drop__hint">PNG, JPG or video — multiple files allowed</span>
        </label>

        {AddDesignStudios.errors.image && AddDesignStudios.touched.image && (
          <span className="al-err">{AddDesignStudios.errors.image}</span>
        )}
      </div>

      {mediaPreviews.length > 0 && (
        <div className="al-field">
          <label className="al-label">
            Selected ({mediaPreviews.length})
          </label>
          <div className="al-picker">
            {mediaPreviews.map((media, index) => (
              <div key={index} style={{ position: "relative" }}>
                <div className="al-picker__i" style={{ cursor: "default" }}>
                  {media.type === "image" ? (
                    <img src={media.url} alt={`preview-${index}`} />
                  ) : (
                    <video
                      src={media.url}
                      controls
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="al-picker__x"
                  title={`Remove ${media.name}`}
                  onClick={() => {
                    const updatedPreviews = mediaPreviews.filter(
                      (_, i) => i !== index
                    );
                    const updatedFiles = files.filter((_, i) => i !== index);
                    setMediaPreviews(updatedPreviews);
                    setFiles(updatedFiles);
                    AddDesignStudios.setFieldValue("image", updatedFiles);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddGraphicDesignModal;
